import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Phase 8 screen-capture protections for PII/content views:
 * - Auto-blur when the tab loses visibility (defeats screen-share leaks)
 * - Text selection, right-click and print disabled
 * - Invisible per-user watermark (leaks are traceable)
 * Best-effort for a PWA — OS-level screenshot blocking isn't possible on the web.
 */
export const ScreenGuard = ({ children, watermark = true }) => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const sync = () => setHidden(document.visibilityState !== "visible");
    const show = () => setHidden(false);
    // Save/print shortcuts blocked; PrintScreen flashes the shield so captures come up blank(ish).
    const keys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["s", "p"].includes((e.key || "").toLowerCase())) e.preventDefault();
    };
    const keyup = (e) => {
      if (e.key === "PrintScreen") {
        setHidden(true);
        try { navigator.clipboard.writeText(""); } catch { /* no-op */ }
        setTimeout(() => setHidden(false), 1500);
      }
    };
    document.addEventListener("visibilitychange", sync);
    document.addEventListener("keydown", keys);
    document.addEventListener("keyup", keyup);
    // Android PWAs (esp. Samsung) sometimes skip visibilitychange on app resume,
    // leaving the shield stuck — clear it on any "we're back" signal.
    window.addEventListener("focus", show);
    window.addEventListener("pageshow", show);
    document.addEventListener("resume", show);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      document.removeEventListener("keydown", keys);
      document.removeEventListener("keyup", keyup);
      window.removeEventListener("focus", show);
      window.removeEventListener("pageshow", show);
      document.removeEventListener("resume", show);
    };
  }, []);

  // Fail-safe: while the overlay is up, poll — if the page is actually visible,
  // drop the shield even when no resume event ever arrives.
  useEffect(() => {
    if (!hidden) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") setHidden(false);
    }, 800);
    return () => clearInterval(t);
  }, [hidden]);

  const wm = user ? `${user.name || user.email || ""} · ${String(user.id || "").slice(0, 8)}` : "";
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='320' height='200'><text x='20' y='110' transform='rotate(-22 160 100)' font-family='Arial' font-size='13' fill='%230A0D14' fill-opacity='0.038'>${encodeURIComponent(wm)}</text></svg>`;

  return (
    <div className="relative screen-guard" onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => { if (e.target.tagName === "IMG" || e.target.tagName === "VIDEO") e.preventDefault(); }}
      data-testid="screen-guard">
      {children}
      {watermark && user && (
        <div aria-hidden data-testid="watermark-layer"
          className="pointer-events-none fixed inset-0 z-[45]"
          style={{ backgroundImage: `url("data:image/svg+xml,${svg}")`, backgroundRepeat: "repeat" }} />
      )}
      {hidden && (
        <div data-testid="unfocus-blur-overlay" onClick={() => setHidden(false)}
          className="fixed inset-0 z-[200] backdrop-blur-2xl bg-white/70 grid place-items-center cursor-pointer">
          <div className="text-center px-6">
            <div className="font-display font-extrabold text-xl text-[#000000]">Content hidden</div>
            <div className="text-sm text-[#000000] mt-1">Tap anywhere to keep reading.</div>
          </div>
        </div>
      )}
    </div>
  );
};
